import {
	FrontendBatchType,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestExpectationField,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestResultField,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleInstance,
	FrontendSampleTestInformation,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import StarIcon from "@mui/icons-material/Star";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import {
	getShortTurnaroundTime,
	getTurnaroundTimeBackgroundColor,
} from "../../../helpers/sampleHelper";
import {
	getEnabledHierarchyNodeTree,
	renderEnabledTestInfoSection,
} from "../../../helpers/testHierarchyHelper";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	selectedTestCategory: FrontendCompanyTestCategory;
	hierarchyNodeParentMap: Map<string, FrontendCompanyTestHierarchyNode[]>;
	instance: FrontendSampleInstance;
	batchType: FrontendBatchType;
	isBatched: boolean;
	isSelectedAsVerifySample: boolean;
	selectAsInstanceToVerify: () => void;
	requireDuplicateInstance: boolean;
	openSpecReadingDrawer: () => void;
}

export default function InstanceInfoCardContent(props: Props) {
	const { t: translation } = useTranslation();

	const {
		company,
		selectedTestCategory,
		hierarchyNodeParentMap,
		instance,
		batchType,
		isSelectedAsVerifySample,
		isBatched,
		selectAsInstanceToVerify,
		requireDuplicateInstance,
		openSpecReadingDrawer,
	} = props;

	const sampleTestInfo: FrontendSampleTestInformation =
		instance.sampleTestInfo as FrontendSampleTestInformation;
	const TATBackgroundColor: string = getTurnaroundTimeBackgroundColor(
		sampleTestInfo.turnaroundTime,
	);

	const selectedMatrix: FrontendCompanySampleMatrix =
		selectedTestCategory.sampleMatrices.find((matrix) => {
			return matrix.matrixId === instance.sample.matrixId;
		}) as FrontendCompanySampleMatrix;

	const selectedNodeIds: Array<string> = sampleTestInfo.selectedTests.map(
		(test) => {
			return test.selectedNodeId;
		},
	);
	const hierarchyTreeRootNode: TestHierarchyTreeNode =
		getEnabledHierarchyNodeTree(selectedNodeIds, hierarchyNodeParentMap);

	const expectationFieldIdToNameMap = new Map<string, string>();
	for (const field of company.testResultAndExpectationFields) {
		const expectationField: FrontendCompanyTestExpectationField | undefined =
			field.expectationField;
		if (!expectationField) {
			continue;
		}
		const fieldId: string = expectationField.expectationFieldId;
		if (field) {
			expectationFieldIdToNameMap.set(fieldId, field.fieldName);
		}
	}

	const fieldsHavingExpectations: FrontendCompanyTestResultField[] =
		company.testResultAndExpectationFields.filter((resultField) => {
			return resultField.expectationField != undefined;
		});

	const testInfoNode: Array<React.ReactNode> = renderEnabledTestInfoSection(
		hierarchyTreeRootNode,
		"typography-body-02",
		fieldsHavingExpectations,
		sampleTestInfo.expectationInputs,
		expectationFieldIdToNameMap,
		company.testResultUnits,
		company.hierarchyNodeMappingPackages,
	);

	const instanceBody: React.ReactNode = (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			{" "}
			{requireDuplicateInstance ? (
				isSelectedAsVerifySample ? (
					<div className="batch-instance-selected-as-verify-row typography-body-01">
						<StarIcon />
						{translation("SelectedAsVerifyInstance")}
					</div>
				) : isBatched ? (
					<div
						className="batch-instance-selected-as-verify-action typography-body-01"
						onClick={selectAsInstanceToVerify}
					>
						<StarIcon />
					</div>
				) : (
					<div style={{ minHeight: "27px" }}></div>
				)
			) : (
				<></>
			)}
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("SampleId")}
				</div>
				<div
					className="overflow-ellipsis-text"
					style={{ maxWidth: "70%", display: "flex", gap: "5px" }}
				>
					{instance.sample.fullSampleId}
					<div
						className="typography-caption turnaround-time-box"
						style={{ backgroundColor: TATBackgroundColor }}
					>
						{getShortTurnaroundTime(sampleTestInfo.turnaroundTime, translation)}
					</div>
					{instance.isRetest ? (
						<div
							className="typography-caption turnaround-time-box"
							style={{ backgroundColor: TATBackgroundColor }}
						>
							{translation("Retest")}
						</div>
					) : (
						<></>
					)}
					{instance.isAdditionalTest ? (
						<div
							className="typography-caption turnaround-time-box"
							style={{ backgroundColor: TATBackgroundColor }}
						>
							{translation("Add")}
						</div>
					) : (
						<></>
					)}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Matrix")}
				</div>
				<div style={{ maxWidth: "50%", textAlign: "right" }}>
					{instance.sample.isCustomMatrix
						? instance.sample.matrix
						: selectedMatrix.subMatrix}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("TestCategory")}
				</div>
				<div style={{ maxWidth: "50%", textAlign: "right" }}>
					{selectedTestCategory.testCategoryName}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("SampleSize")}
				</div>
				<div style={{ maxWidth: "50%", textAlign: "right" }}>
					{`${instance.weight} ${instance.weightUnit}`}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("DueDate")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{formatCustomDate(instance.expectedCompletionTime as Date)}
				</div>
			</div>
			{instance.specification && (
				<div className="order-cell-body-row typography-body-02">
					<div className="sample-info-row-description">{`${translation(
						"Specification",
					)}`}</div>
					<div
						className=" overflow-ellipsis-text link-like-text"
						onClick={() => {
							openSpecReadingDrawer();
						}}
					>
						{`${instance.specification.fileName}.${instance.specification.format}`}
					</div>
				</div>
			)}
			{batchType.batchTypeId == "other" ? (
				<div className="order-cell-body-row typography-body-02">
					<div className="sample-info-row-description">
						{translation("Test")}
					</div>
					<div style={{ maxWidth: "70%", textAlign: "right" }}>
						{sampleTestInfo.additionalTestDescription}
					</div>
				</div>
			) : (
				testInfoNode
			)}
		</div>
	);

	return instanceBody;
}
